import { useMemo } from 'react';
import {
  useFocusedIds,
  useIsFocusActive,
} from '@components/MainStage/focusOn.store';
import useStore from '@components/MainStage/store';
import { NodesTypes } from '@constants/canvas/general';
import {
  CiaSubLayers,
  ConceptualSubLayerTypes,
  ContextualSubLayerTypes,
  InitiativesSubLayerTypes,
  LogicalSubLayerTypes,
  SubLayersWithoutValidStatus,
  SubLayerTypes,
} from '@constants/canvas/layers';
import { useProject } from '@context/Project/ProjectProvider';
import { useProjectFilters } from '@context/ProjectFilters/ProjectFiltersProvider';
import { NodeDTOTypes } from '@store/services/nodes/types';
import { useQuestionnaireResult } from '@store/zustand/questionnaire-result';
import { isCriteriaFieldsMatch } from '@utils/canvasHelpers';
import {
  checkScopeFilter,
  SCOPE_FILTER_NO_OPERATIONALISED,
} from '@utils/domains';
import { traverseNodes } from '@views/Insights/Stage/helpers';
import { Tabs } from '@views/Insights/types';

const useFilteredCanvas = ({
  applyCollapse = true,
}: { applyCollapse?: boolean } = {}) => {
  const { edges, nodes, phantoms } = useStore();
  const isFocusActive = useIsFocusActive();
  const focusedIds = useFocusedIds();
  const { insightsTab } = useProject();
  const { result: questionnaireResult } = useQuestionnaireResult();

  const {
    toolbox: { domains },
  } = useProject();

  const {
    reviewStatus,
    validationStatus,
    ciaFilters,
    operationalStatuses,
    operatingModelStatuses,
    partners,
  } = useProjectFilters();

  return useMemo(() => {
    const filteredCanvas = { nodes, edges };
    const connectedBANodes = new Set<string>();

    const isTabFocusedOnOrModelEvolution = [
      Tabs.FocusOn,
      Tabs.ModelEvolution,
    ].includes(insightsTab);

    filteredCanvas.nodes = filteredCanvas.nodes.map((node) => ({
      ...node,
      data: {
        ...node.data,
        canvas: {
          ...node.data.canvas,
          hiddenByFilters: false,
        },
      },
    }));

    if (!isTabFocusedOnOrModelEvolution) {
      filteredCanvas.nodes = filteredCanvas.nodes.map((node) => ({
        ...node,
        hidden: applyCollapse ? node.data.canvas.collapsed ?? false : false,
      }));
    } else if (!applyCollapse) {
      filteredCanvas.nodes = filteredCanvas.nodes.map((node) =>
        node.data.canvas.collapsed ? { ...node, hidden: false } : node,
      );
    }

    // Domain Scope Filter
    if (domains.length) {
      filteredCanvas.nodes = filteredCanvas.nodes.map((node) => {
        const isCollapsed =
          applyCollapse && node.data?.canvas?.collapsed === true;
        if (isCollapsed) return node;

        const isCustomNode = node.type === NodesTypes.CustomNode;

        const isScopeIncluded = checkScopeFilter(
          domains,
          node.data?.dto?.type,
          node.data?.dto?.domainNodes,
        );

        const isDifferentDomain = isCustomNode && !isScopeIncluded;

        const shouldHideNode =
          (node.hidden && isTabFocusedOnOrModelEvolution) || isDifferentDomain;

        return {
          ...node,
          hidden: shouldHideNode,
          data: {
            ...node.data,
            canvas: {
              ...node.data.canvas,
              hiddenByFilters: isDifferentDomain,
            },
          },
        };
      });
    }

    if (domains.includes(SCOPE_FILTER_NO_OPERATIONALISED)) {
      const noOperationalisedAncestorIds = new Set<string>();

      const visited = new Set<string>();

      const ANCESTOR_TYPES = new Set([
        NodeDTOTypes.SecurityAttribute,
        NodeDTOTypes.BusinessAttribute,
        NodeDTOTypes.StrategicEnabler,
        NodeDTOTypes.StrategicPriority,
      ]);

      const traverseParents = (nodeId: string) => {
        if (visited.has(nodeId)) return;
        visited.add(nodeId);
        const found = nodes.find((n) => n.id === nodeId);
        found?.data?.dto?.parentsIds?.forEach((parentId: string) => {
          const parentNode = nodes.find((n) => n.id === parentId);
          if (!parentNode || !ANCESTOR_TYPES.has(parentNode.data?.dto?.type))
            return;
          noOperationalisedAncestorIds.add(parentId);
          traverseParents(parentId);
        });
      };

      filteredCanvas.nodes.forEach((node) => {
        if (
          node.type !== NodesTypes.CustomNode ||
          node.hidden ||
          !checkScopeFilter(
            [SCOPE_FILTER_NO_OPERATIONALISED],
            node.data?.dto?.type,
            node.data?.dto?.domainNodes,
          )
        )
          return;

        traverseParents(node.id);
      });

      if (noOperationalisedAncestorIds.size > 0) {
        filteredCanvas.nodes = filteredCanvas.nodes.map((node) => {
          if (node.data?.canvas?.collapsed === true) return node;

          if (
            node.type !== NodesTypes.CustomNode ||
            !noOperationalisedAncestorIds.has(node.id)
          )
            return node;

          const isAncestorLayer = [
            ContextualSubLayerTypes.StrategicEnablers,
            ContextualSubLayerTypes.StrategicPriorities,
            ConceptualSubLayerTypes.BusinessAttributes,
            ConceptualSubLayerTypes.SecurityAttributes,
          ].includes(node.parentNode as ContextualSubLayerTypes);

          if (!isAncestorLayer) return node;

          return {
            ...node,
            hidden: false,
            data: {
              ...node.data,
              canvas: {
                ...node.data.canvas,
                hiddenByFilters: false,
              },
            },
          };
        });
      }
    }

    // Review Required Filter
    if (reviewStatus.length) {
      filteredCanvas.nodes = filteredCanvas.nodes.map((node) => ({
        ...node,
        hidden:
          node.hidden ||
          !(
            node.type !== NodesTypes.CustomNode ||
            node.data?.dto?.review_required
          ),
        data: {
          ...node.data,
          canvas: {
            ...node.data.canvas,
            hiddenByFilters: !(
              node.type !== NodesTypes.CustomNode ||
              node.data?.dto?.review_required
            ),
          },
        },
      }));
    }

    // Validation Status Filter
    if (validationStatus.length) {
      filteredCanvas.nodes = filteredCanvas.nodes.map((node) => {
        const nodeWithoutValidStatus = SubLayersWithoutValidStatus.includes(
          node.parentNode as SubLayerTypes,
        );

        const isVisible =
          node.type !== NodesTypes.CustomNode ||
          validationStatus.includes(node.data?.dto?.valid_status);

        return {
          ...node,
          hidden: node.hidden || nodeWithoutValidStatus || !isVisible,
          data: {
            ...node.data,
            canvas: {
              ...node.data.canvas,
              hiddenByFilters: nodeWithoutValidStatus || !isVisible,
            },
          },
        };
      });
    }

    // Hide unreported nodes in CIA sublayers when questionnaire result is active
    if (questionnaireResult) {
      filteredCanvas.nodes = filteredCanvas.nodes.map((node) => {
        if (node.type !== NodesTypes.CustomNode) return node;
        if (!CiaSubLayers.includes(node.parentNode as any)) return node;
        if (node.data.dto.is_reported === false) {
          return {
            ...node,
            hidden: true,
            data: {
              ...node.data,
              canvas: {
                ...node.data.canvas,
                hiddenByFilters: true,
              },
            },
          };
        }
        return node;
      });
    }

    // Cia criteria filter
    if (
      Object.values(ciaFilters).some((fields) =>
        Object.values(fields).some((answers) => answers?.length),
      )
    ) {
      filteredCanvas.nodes = filteredCanvas.nodes.map((node) => {
        if (node.type !== NodesTypes.CustomNode) return node;

        if (questionnaireResult) {
          const isCiaSublayer = CiaSubLayers.includes(node.parentNode as any);

          if (!isCiaSublayer) return node;
        }

        const isAssuranceMatch = isCriteriaFieldsMatch(
          ciaFilters.assurance_level,
          node.data.dto.cia?.assurance_level?.criterias,
        );

        const isCoverageMatch = isCriteriaFieldsMatch(
          ciaFilters.coverage,
          node.data.dto.cia?.coverage?.criterias,
        );

        const isApplicabilityMatch = isCriteriaFieldsMatch(
          ciaFilters.applicability,
          node.data.dto.cia?.applicability?.criterias,
        );

        const isComplete = !questionnaireResult || node.data.dto.is_complete;

        const isReported = node.data.dto.is_reported !== false;

        const isAllMatch =
          isAssuranceMatch &&
          isCoverageMatch &&
          isApplicabilityMatch &&
          isComplete &&
          isReported;

        return {
          ...node,
          hidden: node.hidden || !isAllMatch,
          data: {
            ...node.data,
            canvas: {
              ...node.data.canvas,
              hiddenByFilters: !isAllMatch,
            },
          },
        };
      });

      // Hide orphaned physical/component nodes (no visible parent) in questionnaire result mode
      if (questionnaireResult) {
        const orphanCascade = [
          NodeDTOTypes.Capabilities,
          NodeDTOTypes.Functions,
          NodeDTOTypes.Technologies,
        ];

        orphanCascade.forEach((orphanType) => {
          const visibleIds = new Set(
            filteredCanvas.nodes.filter((n) => !n.hidden).map((n) => n.id),
          );
          filteredCanvas.nodes = filteredCanvas.nodes.map((node) => {
            if (node.hidden || node.type !== NodesTypes.CustomNode) return node;
            if (node.data?.dto?.type !== orphanType) return node;
            const parentsIds: string[] = node.data?.dto?.parentsIds ?? [];
            const hasVisibleParent = parentsIds.some((id) =>
              visibleIds.has(id),
            );
            if (hasVisibleParent) return node;
            return {
              ...node,
              hidden: true,
              data: {
                ...node.data,
                canvas: { ...node.data.canvas, hiddenByFilters: true },
              },
            };
          });
        });
      }
    }

    // Operational Status filter
    if (operationalStatuses.length) {
      filteredCanvas.nodes = filteredCanvas.nodes.map((node) => {
        if (node.type !== NodesTypes.CustomNode) return node;

        const opStatus = node.data.dto?.meta_data?.operational_status;

        // The report only excludes "In Development" controls, so nodes without
        // an operational status must stay visible in the questionnaire result
        // (View Controls) flow to match what the report lists.
        const isStatusMatch =
          operationalStatuses.includes(opStatus) ||
          (Boolean(questionnaireResult) && !opStatus);

        const isServiceSublayer =
          node.parentNode === LogicalSubLayerTypes.Services;

        if (isServiceSublayer && !node.hidden && isStatusMatch) {
          traverseNodes(
            questionnaireResult ? filteredCanvas.nodes : nodes,
            connectedBANodes,
            node.id,
            false,
            true,
            !questionnaireResult,
          );
          connectedBANodes.delete(node.id);
        }

        return {
          ...node,
          hidden: node.hidden || !isStatusMatch,
          data: {
            ...node.data,
            canvas: {
              ...node.data.canvas,
              hiddenByFilters: !isStatusMatch,
            },
          },
        };
      });
    }

    // Operating Model filter
    if (operatingModelStatuses.length) {
      filteredCanvas.nodes = filteredCanvas.nodes.map((node) => {
        if (node.type !== NodesTypes.CustomNode) return node;

        const isStatusMatch = operatingModelStatuses.includes(
          node.data.dto?.meta_data?.operational_model,
        );

        return {
          ...node,
          hidden: node.hidden || !isStatusMatch,
          data: {
            ...node.data,
            canvas: {
              ...node.data.canvas,
              hiddenByFilters: !isStatusMatch,
            },
          },
        };
      });
    }

    // Partners filter
    if (partners.length) {
      filteredCanvas.nodes = filteredCanvas.nodes.map((node) => {
        if (node.type !== NodesTypes.CustomNode) return node;

        const isPartnerMatch = partners.some(
          (partner) => node.data.dto?.partners?.includes(partner),
        );

        return {
          ...node,
          hidden: node.hidden || !isPartnerMatch,
          data: {
            ...node.data,
            canvas: {
              ...node.data.canvas,
              hiddenByFilters: !isPartnerMatch,
            },
          },
        };
      });
    }

    const shouldEnforceBAConnectivity =
      connectedBANodes.size > 0 ||
      (Boolean(questionnaireResult) && operationalStatuses.length > 0);

    if (shouldEnforceBAConnectivity) {
      filteredCanvas.nodes = filteredCanvas.nodes.map((node) => {
        if (node.type !== NodesTypes.CustomNode) return node;

        // Keep nodes in a collapsed layer hidden even when a connected service
        // is visible, otherwise they overlap the layer below.
        if (applyCollapse && node.data?.canvas?.collapsed === true) return node;

        const isContextualOrConceptual = [
          ContextualSubLayerTypes.StrategicEnablers,
          ContextualSubLayerTypes.StrategicPriorities,
          ConceptualSubLayerTypes.BusinessAttributes,
          ConceptualSubLayerTypes.SecurityAttributes,
        ].includes(node.parentNode as ContextualSubLayerTypes);

        if (!isContextualOrConceptual) return node;

        const isComplete = !questionnaireResult || node.data?.dto?.is_complete;
        const isVisible = connectedBANodes.has(node.id) && isComplete;

        return {
          ...node,
          hidden: !isVisible,
          data: {
            ...node.data,
            canvas: {
              ...node.data.canvas,
              hiddenByFilters: !isVisible,
              hiddenBA: !isVisible,
            },
          },
        };
      });
    }

    // Resolve Change initiatives last, against the final visible node set, so a CI whose parents were hidden
    // by any earlier filter is hidden too
    if (questionnaireResult) {
      const visibleIds = new Set(
        filteredCanvas.nodes.filter((n) => !n.hidden).map((n) => n.id),
      );

      filteredCanvas.nodes = filteredCanvas.nodes.map((node) => {
        if (node.type !== NodesTypes.CustomNode) return node;
        if (node.parentNode !== InitiativesSubLayerTypes.ChangeInitiatives) {
          return node;
        }

        const parentsIds: string[] = node.data?.dto?.parentsIds ?? [];
        const hasVisibleParent = parentsIds.some((id) => visibleIds.has(id));
        const isVisible = node.data.dto.is_complete && hasVisibleParent;

        return {
          ...node,
          hidden: node.hidden || !isVisible,
          data: {
            ...node.data,
            canvas: { ...node.data.canvas, hiddenByFilters: !isVisible },
          },
        };
      });
    }

    if (isFocusActive) {
      const focusedSet = new Set(focusedIds);

      filteredCanvas.nodes = filteredCanvas.nodes.map((node) => {
        if (node.type !== NodesTypes.CustomNode) return node;

        const outsideFocus = !focusedSet.has(node.id);

        return {
          ...node,
          ...(outsideFocus ? { selectable: false, focusable: false } : {}),
          data: {
            ...node.data,
            canvas: {
              ...node.data.canvas,
              disabled: node.data.canvas.disabled || outsideFocus,
              preventSelection:
                node.data.canvas.preventSelection || outsideFocus,
            },
          },
        };
      });
    }

    filteredCanvas.edges = filteredCanvas.edges
      .concat(phantoms)
      .map((edge) => ({
        ...edge,
        hidden: filteredCanvas.nodes.some(
          ({ id, hidden }) =>
            hidden && (id === edge.source || id === edge.target),
        ),
      }));

    return filteredCanvas;
  }, [
    nodes,
    edges,
    domains,
    reviewStatus,
    validationStatus,
    phantoms,
    insightsTab,
    ciaFilters,
    questionnaireResult,
    operatingModelStatuses,
    operationalStatuses,
    partners,
    isFocusActive,
    focusedIds,
    applyCollapse,
  ]);
};

export default useFilteredCanvas;
